import { useContext } from 'react';

import { CommandPaletteDerivedQueryContext } from '../components/command-palette-derived-query-context';
import { useInternalCommandPaletteState } from '../components/command-palette-state-provider';

/**
 * Narrowed public read of palette state.
 *
 * Exposes the three fields a consumer hook needs to author commands
 * that respond to palette activity: `isOpen` (gate registration on
 * open-only paths), `query` (key React Query against the live input),
 * and `rootCommandId` (decide whether to fetch -- "this is the
 * entered root, so I should fetch" vs ambient slot semantics).
 *
 * The full internal state shape (view command, prefill, timing
 * fields, lastOpenTrigger) is intentionally not exposed. Consumers
 * that need any of that are doing palette-internal work; they should
 * live inside the package.
 *
 * @example Reactive scope hook
 * ```ts
 * function useSearchFlagsCommands() {
 *   const SCOPE_ID = 'search-flags';
 *   const { query, rootCommandId } = useCommandPaletteState();
 *   const isRoot = rootCommandId === SCOPE_ID;
 *
 *   const { data: flags = [] } = useQuery({
 *     queryKey: ['palette', 'flags', query],
 *     queryFn: ({ signal }) => searchFlags(query, signal),
 *     enabled: isRoot && query.trim() !== '',
 *     placeholderData: keepPreviousData,
 *   });
 *
 *   useCommands(
 *     flags.map((f) => link({ id: f.key, label: f.name, href: `/flags/${f.key}`, keywords: [f.key], parent: SCOPE_ID })),
 *     [flags],
 *   );
 * }
 * ```
 */
export function useCommandPaletteState(): {
  isOpen: boolean;
  query: string;
  rootCommandId: string | null;
} {
  const state = useInternalCommandPaletteState();
  const derived = useContext(CommandPaletteDerivedQueryContext);
  const isOpen = state.mode.type !== 'closed';
  const rootCommandId = state.mode.type === 'open' ? state.mode.rootCommandId : null;
  // `query` is the DEBOUNCED value, not the raw input. Consumers key
  // their fetches off this, so a burst of typing produces a single
  // fetch instead of one per keystroke. The live, per-keystroke value
  // is intentionally unreachable here -- it stays internal to the
  // input, snap parsing, and local ranking. Falls back to the live
  // query only when the derived provider isn't mounted (e.g. a unit
  // test that renders the hook in isolation).
  const liveQuery = state.mode.type === 'open' ? state.mode.query : '';
  const query = derived?.debouncedQuery ?? liveQuery;
  return { isOpen, query, rootCommandId };
}
